import { ClientUser, DriverEmploymentStatus, DriverPriceType, DriverTypeCategory, UserRole } from '@numeo/types-core'
import {
    TEAM_DRIVER_EXPERIENCE_LEVELS,
    TeamDriverCompensationFilter,
    TeamDriverExperienceLevel,
    TeamDriverRow,
    TeamDriverSortField,
    TeamDriverType,
    TeamListPage,
    TeamMemberRow,
    TeamSortOrder,
} from '@numeo/types-main-web-app'
import { driverRowFromUser, isMemberUser, memberRowFromUser } from './teamRows'

/**
 * The team directory's queries, and the same filter, order and paging `/team/*` applies, over rows the client already
 * holds: the roster for a company the directory does not serve yet, and a cached page a socket change patches. Pure.
 */

/** Rows per page the screens ask for, and the most the server answers: a list is paged and searched on the server, never read whole. */
export const TEAM_PAGE_SIZE = 100
/** The most ids one `ids=` may name (server limit). */
export const TEAM_MAX_IDS = 100
const SEARCH_MIN = 2
const SEARCH_MAX = 64
const MIN_PHONE_DIGITS = 3

export interface TeamMembersQuery {
    page?: number
    limit?: number
    search?: string
    roles?: UserRole[]
    /** Only these people, one page. */
    ids?: string[]
    /** Only the members who linked this mailbox (the server matches it ignoring case): "who else holds this mailbox". */
    mailbox?: string
    /** Only the members with at least one mailbox linked. */
    mailboxConnected?: boolean
}

export interface TeamDriversQuery {
    page?: number
    limit?: number
    search?: string
    /** The trucks whose unit number the search matched (resolved from the fleet here): their drivers match it too. */
    searchTruckIds?: string[]
    status?: DriverEmploymentStatus
    /** A truck id, `none` for drivers on no truck, or `any` for drivers on one. */
    truck?: string
    hasCoDriver?: boolean
    /** Only the drivers that name this driver as their co-driver. */
    coDriverOf?: string
    /** Leave terminated drivers out. */
    hideTerminated?: boolean
    /** Experience buckets; a driver with no years recorded matches any. */
    experience?: TeamDriverExperienceLevel[]
    /** Pay tier only: the server refuses it to anyone else. */
    compensation?: TeamDriverCompensationFilter
    driverType?: TeamDriverType
    /** Absent: by name. */
    sort?: TeamDriverSortField
    /** Absent: ascending. */
    order?: TeamSortOrder
    /** Only these drivers, one page. */
    ids?: string[]
    /**
     * What the screen would otherwise ask for separately, answered with the page: `counts` (everyone, the search and panel's
     * share, the favourites among them) and `partners` (the page's co-drivers). Only the directory answers it.
     */
    include?: TeamDriversInclude[]
    /** With `counts`: the filter panel's truck filter, which the counts honour while the tab's own narrowing is left out. */
    countsTruck?: 'any' | 'none'
    /** With `counts`: the favourites to count. */
    countFavorites?: string[]
}

export type TeamDriversInclude = 'counts' | 'partners'

const uniqueSorted = <T extends string | number>(values: readonly T[] | undefined): T[] | undefined =>
    values ? [...new Set(values)].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0)) : undefined

/** The search the server accepts: trimmed, at most 64 characters, and none at all below 2. */
const normalizeSearch = (search: string | undefined): string | undefined => {
    const trimmed = search?.trim().slice(0, SEARCH_MAX)
    return trimmed && trimmed.length >= SEARCH_MIN ? trimmed : undefined
}

/**
 * One spelling per query, so equal questions share a cache entry and the server never refuses one: defaults dropped,
 * ids and roles de-duplicated and sorted (at most `TEAM_MAX_IDS` ids), the search trimmed.
 */
export const normalizeMembersQuery = (query: TeamMembersQuery = {}): TeamMembersQuery => {
    const search = normalizeSearch(query.search)
    const roles = uniqueSorted(query.roles)
    const ids = uniqueSorted(query.ids?.filter(Boolean).map(String))?.slice(0, TEAM_MAX_IDS)
    // One spelling of an address: the server matches it ignoring case anyway.
    const mailbox = query.mailbox?.trim().toLowerCase()
    return {
        ...(ids ? { ids } : { page: Math.max(1, query.page ?? 1), limit: Math.min(TEAM_PAGE_SIZE, Math.max(1, query.limit ?? TEAM_PAGE_SIZE)) }),
        ...(search ? { search } : {}),
        ...(roles?.length ? { roles } : {}),
        ...(mailbox ? { mailbox } : {}),
        ...(query.mailboxConnected ? { mailboxConnected: true } : {}),
    }
}

/** Name ascending is the server's default order, so it is never spelled out. */
const normalizeSort = (sort: TeamDriverSortField | undefined, order: TeamSortOrder | undefined): Pick<TeamDriversQuery, 'sort' | 'order'> => ({
    ...(sort && sort !== 'name' ? { sort } : {}),
    ...(order === 'desc' ? { order } : {}),
})

export const normalizeDriversQuery = (query: TeamDriversQuery = {}): TeamDriversQuery => {
    const search = normalizeSearch(query.search)
    const ids = uniqueSorted(query.ids?.filter(Boolean).map(String))?.slice(0, TEAM_MAX_IDS)
    const truck = query.truck?.trim()
    const experience = TEAM_DRIVER_EXPERIENCE_LEVELS.filter((level) => query.experience?.includes(level))
    // They only widen a search: with none, there is nothing to send them with.
    const searchTruckIds = search ? uniqueSorted(query.searchTruckIds?.filter(Boolean).map(String))?.slice(0, TEAM_MAX_IDS) : undefined
    return {
        ...(ids ? { ids } : { page: Math.max(1, query.page ?? 1), limit: Math.min(TEAM_PAGE_SIZE, Math.max(1, query.limit ?? TEAM_PAGE_SIZE)) }),
        ...(search ? { search } : {}),
        ...(searchTruckIds?.length ? { searchTruckIds } : {}),
        ...(query.status ? { status: query.status } : {}),
        ...(truck ? { truck } : {}),
        ...(query.hasCoDriver !== undefined ? { hasCoDriver: query.hasCoDriver } : {}),
        ...(query.coDriverOf ? { coDriverOf: query.coDriverOf } : {}),
        ...(query.hideTerminated ? { hideTerminated: true } : {}),
        ...(experience.length ? { experience } : {}),
        ...(query.compensation ? { compensation: query.compensation } : {}),
        ...(query.driverType ? { driverType: query.driverType } : {}),
        ...normalizeSort(query.sort, query.order),
        ...normalizeInclude(query),
    }
}

/** `include` and its options in one spelling: the includes sorted, the options only with `counts`, at most `TEAM_MAX_IDS` favourites. */
const normalizeInclude = (query: TeamDriversQuery): Pick<TeamDriversQuery, 'include' | 'countsTruck' | 'countFavorites'> => {
    const include = uniqueSorted(query.include)
    if (!include?.length) return {}
    const counts = include.includes('counts')
    const countFavorites = counts && query.countFavorites ? uniqueSorted(query.countFavorites.filter(Boolean).map(String))?.slice(0, TEAM_MAX_IDS) : undefined
    return {
        include,
        ...(counts && query.countsTruck ? { countsTruck: query.countsTruck } : {}),
        ...(countFavorites?.length ? { countFavorites } : {}),
    }
}

/** Name order without regard to case, then id: the server's order (en, strength 2). A row without a name sorts first. */
export const compareByName = (a: { id: string; name?: string }, b: { id: string; name?: string }): number =>
    (a.name ?? '').localeCompare(b.name ?? '', 'en', { sensitivity: 'base' }) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)

const contains = (value: string | undefined, search: string): boolean => !!value && value.toLowerCase().includes(search.toLowerCase())

/** The server's "contains" over name and email; with 3 or more digits, also the phone whatever its formatting. */
const matchesSearch = (row: { name?: string; email?: string; phone?: string }, search: string, withPhone: boolean): boolean => {
    if (contains(row.name, search) || contains(row.email, search)) return true
    const digits = search.replace(/\D/g, '')
    return withPhone && digits.length >= MIN_PHONE_DIGITS && !!row.phone && row.phone.replace(/\D/g, '').includes(digits)
}

export const matchesMemberQuery = (row: TeamMemberRow, query: TeamMembersQuery): boolean =>
    (!query.ids || query.ids.includes(row.id)) &&
    (!query.roles?.length || query.roles.includes(row.role)) &&
    (!query.search || matchesSearch(row, query.search, false)) &&
    (!query.mailbox || !!row.mailboxes?.some((mailbox) => mailbox.toLowerCase() === query.mailbox?.toLowerCase())) &&
    (!query.mailboxConnected || !!row.mailboxConnected)

const isEmpty = (value: string | undefined): boolean => !value

/** The server's experience buckets: under 1 year, 1 to under 5, 5 and more. No years recorded (none or 0) is in every one. */
const inExperience = (years: number | undefined, levels: readonly TeamDriverExperienceLevel[]): boolean => {
    if (!years) return true
    if (years < 1) return levels.includes('new')
    if (years < 5) return levels.includes('experienced')
    return levels.includes('veteran')
}

const COMPENSATION_TYPES: Record<TeamDriverCompensationFilter, DriverPriceType> = {
    perMile: DriverPriceType.PER_MILE,
    percentage: DriverPriceType.PERCENT,
}

const matchesTruck = (truckId: string | undefined, truck: string): boolean => {
    if (truck === 'none') return isEmpty(truckId)
    if (truck === 'any') return !isEmpty(truckId)
    return truckId === truck
}

/** `team`: a co-driver; `solo`: none, and no team contract either. */
const matchesDriverType = (row: TeamDriverRow, type: TeamDriverType): boolean =>
    type === 'team' ? !isEmpty(row.coDriverId) : isEmpty(row.coDriverId) && row.contract?.driverType !== DriverTypeCategory.TEAM

/** Every filter of `GET /team/drivers/v1`, evaluated on a row as the server evaluates it on the stored driver. */
export const matchesDriverQuery = (row: TeamDriverRow, query: TeamDriversQuery): boolean => {
    if (query.ids && !query.ids.includes(row.id)) return false
    if (query.status) {
        const status = row.employmentStatus
        const ok = query.status === DriverEmploymentStatus.ACTIVE ? !status || status === DriverEmploymentStatus.ACTIVE : status === query.status
        if (!ok) return false
    }
    if (query.hideTerminated && row.employmentStatus === DriverEmploymentStatus.TERMINATED) return false
    if (query.truck && !matchesTruck(row.truckId, query.truck)) return false
    if (query.hasCoDriver !== undefined && query.hasCoDriver === isEmpty(row.coDriverId)) return false
    if (query.coDriverOf && row.coDriverId !== query.coDriverOf) return false
    if (query.driverType && !matchesDriverType(row, query.driverType)) return false
    if (query.experience?.length && !inExperience(row.experienceInYears, query.experience)) return false
    if (query.compensation && row.compensation?.type !== COMPENSATION_TYPES[query.compensation]) return false
    if (!query.search) return true
    // As the server searches a driver: name, email, phone, the ELD company, and the trucks the search matched.
    return matchesSearch(row, query.search, true) || contains(row.eldCompany, query.search) || (!!row.truckId && !!query.searchTruckIds?.includes(row.truckId))
}

type SortValue = string | undefined

/** The values each sortable column orders on, in the server's field order (`driverDirectorySort`). */
const SORT_VALUES: Record<TeamDriverSortField, (row: TeamDriverRow) => SortValue[]> = {
    name: (row) => [row.name],
    contact: (row) => [row.email, row.phone],
    company: (row) => [row.eldCompany],
    label: (row) => [row.contract?.driverRole, row.label],
    employment: (row) => [row.employmentStatus],
    terminationDate: (row) => [row.terminationDate],
}

/** One value under the server's collation: a missing one first, text without regard to case (ISO dates order as text). */
const compareValue = (a: SortValue, b: SortValue): number => {
    if (!a || !b) return (a ? 1 : 0) - (b ? 1 : 0)
    return a.localeCompare(b, 'en', { sensitivity: 'base' })
}

/** The order `GET /team/drivers/v1` serves for this query: the column's fields, then the name, then the id, one direction. */
export const driverComparator = (query: Pick<TeamDriversQuery, 'sort' | 'order'>): ((a: TeamDriverRow, b: TeamDriverRow) => number) => {
    const values = SORT_VALUES[query.sort ?? 'name']
    const direction = query.order === 'desc' ? -1 : 1
    return (a, b) => {
        const left = values(a)
        const right = values(b)
        for (let index = 0; index < left.length; index += 1) {
            const order = compareValue(left[index], right[index])
            if (order) return direction * order
        }
        return direction * compareByName(a, b)
    }
}

/** One page of already filtered rows, in the server's order and paginated shape. */
const pageOf = <T extends { id: string; name?: string }>(
    rows: readonly T[],
    query: { page?: number; limit?: number; ids?: string[] },
    compare: (a: T, b: T) => number = compareByName
): TeamListPage<T> => {
    const sorted = [...rows].sort(compare)
    if (query.ids) return { items: sorted, pagination: { total: sorted.length, page: 1, limit: Math.max(query.ids.length, 1) } }
    const page = query.page ?? 1
    const limit = query.limit ?? TEAM_PAGE_SIZE
    return { items: sorted.slice((page - 1) * limit, page * limit), pagination: { total: sorted.length, page, limit } }
}

/** `GET /team/members/v1` answered from the roster. */
export const listMembersFromUsers = (
    users: readonly ClientUser[] | undefined,
    ownerId: string | undefined,
    query: TeamMembersQuery
): TeamListPage<TeamMemberRow> =>
    pageOf(
        (users ?? [])
            .filter((user) => isMemberUser(user) && !user.deletedAt)
            .map((user) => memberRowFromUser(user, ownerId))
            .filter((row) => matchesMemberQuery(row, query)),
        query
    )

/** `GET /team/drivers/v1` answered from the roster (or the scoped driver roster). */
export const listDriversFromUsers = (users: readonly ClientUser[] | undefined, query: TeamDriversQuery): TeamListPage<TeamDriverRow> =>
    pageOf(
        (users ?? [])
            .filter((user) => user.role === UserRole.DRIVER && !user.deletedAt)
            .map(driverRowFromUser)
            .filter((row) => matchesDriverQuery(row, query)),
        query,
        driverComparator(query)
    )
